import { Fragment, FunctionComponent, ReactElement, useState } from 'react'
import { useHistory } from 'react-router-dom'
import {
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  rucksack,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@black-bear-energy/rucksack'
import { DriveFileMoveRounded } from '@mui/icons-material'
import {
  DocumentRequirement,
  DocumentRequirementsChecklist,
  SiteRFPDetails,
} from '@black-bear-energy/black-bear-energy-common'
import { requirementIsChecklist } from 'components/helpers/Sites/requirementIsChecklist'
import { DocumentRequirementRow } from './DocumentRequirementRow'
import { DocumentRequirementPlaceholderRow } from './DocumentRequirementPlaceholderRow'

export const DocumentRequirementsCard: FunctionComponent<
  DocumentRequirementsCardProps
> = ({
  docRequirements = [],
  customerRFPId,
  siteRFPId,
  hasContractsType,
  isFetching,
}) => {
  const [includeVerified, setIncludeVerified] = useState(false)
  const history = useHistory()

  const verifiedCounts = docRequirements.reduce(
    (acc, req) => acc + getVerifiedDocCount(req),
    0
  )
  const verifiedButtonText = includeVerified
    ? `Hide Verified (${verifiedCounts})`
    : `Show Verified (${verifiedCounts})`

  const userMsg = !hasContractsType
    ? 'Contract Type for this Site has not yet been determined.  Document requirement details can be viewed once this has be assigned.'
    : docRequirements.length == 0
    ? 'There are no required documents for this site at this time.'
    : null

  return (
    <Card>
      <CardContent>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            alignSelf: 'stretch',
          }}
        >
          <Typography variant="h6" sx={{ flex: '1 0 0' }}>
            Document Requirements
          </Typography>
          <Button
            sx={{ color: rucksack.palette.primary.dark }}
            onClick={() => setIncludeVerified((prevVerified) => !prevVerified)}
            disabled={!isFetching && !!userMsg}
          >
            {verifiedButtonText}
          </Button>
          <IconButton
            disabled={!customerRFPId}
            onClick={() => {
              if (!customerRFPId) {
                return
              }
              history.push(
                `/document-exchange/projects/${customerRFPId}/sites/${siteRFPId}`
              )
            }}
          >
            <DriveFileMoveRounded
              sx={{
                color: rucksack.palette.primary.main,
              }}
            />
          </IconButton>
        </Box>
        <TableContainer>
          <Table
            className={includeVerified ? '' : 'HideVerified'}
            sx={{
              '& .MuiTableCell-root': { padding: '6px 16px' },
              '& .MuiTableRow-root': { height: '28px' },
              '&.HideVerified .VerifiedRow': { display: 'none' },
            }}
          >
            <TableHead>
              <TableRow sx={{ '& .MuiTableCell-root': { fontSize: '14px' } }}>
                <TableCell key="doc" sx={{ width: '25%' }}>
                  Checklist Item
                </TableCell>
                <TableCell key="upload" align="center" sx={{ width: '25%' }}>
                  Upload Status
                </TableCell>
                <TableCell key="review" align="center" sx={{ width: '25%' }}>
                  Review Status
                </TableCell>
                <TableCell key="view" align="center" sx={{ width: '25%' }}>
                  View Document
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isFetching &&
                new Array(15).fill(0).map((row, rowIndex) => (
                  <TableRow key={rowIndex}>
                    {new Array(4).fill(0).map((cell, cellIndex) => (
                      <TableCell key={cellIndex} sx={{ display: 'table-cell' }}>
                        <Skeleton sx={{ height: '17px', transform: 'none' }} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}

              {!isFetching &&
                hasContractsType &&
                docRequirements.length > 0 &&
                docRequirements.map((req) => getDocumentRequirementRow(req, 0))}
            </TableBody>
          </Table>
        </TableContainer>
        {!isFetching && userMsg && (
          <Box
            sx={{
              display: 'flex',
              height: '288px',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '8px',
              alignSelf: 'stretch',
            }}
          >
            <Typography
              variant="body1"
              sx={{
                fontStyle: 'italic',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
              }}
            >
              {userMsg}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  )
}

function getDocumentRequirementRow(
  req: DocumentRequirementsChecklist | DocumentRequirement,
  depth: number
): ReactElement {
  if (requirementIsChecklist(req)) {
    return (
      <Fragment key={req.name}>
        <DocumentRequirementRow
          requirement={req}
          depth={depth}
          isLeaf={false}
        />
        {req.children.map((child) =>
          getDocumentRequirementRow(child, depth + 1)
        )}
        {req.isDocumentCollection === true && req.children.length === 0 && (
          <DocumentRequirementPlaceholderRow
            key={`${req.name}-collection`}
            depth={depth + 1}
          />
        )}
      </Fragment>
    )
  }
  return (
    <DocumentRequirementRow
      key={req.id}
      requirement={req}
      depth={depth}
      isLeaf={true}
    />
  )
}

function getVerifiedDocCount(
  docRequirement: DocumentRequirementsChecklist | DocumentRequirement
): number {
  if (requirementIsChecklist(docRequirement)) {
    let sum = 0
    for (const child of docRequirement.children) {
      sum += getVerifiedDocCount(child)
    }
    return sum
  }

  return docRequirement.defaultHidden ? 1 : 0
}

export interface DocumentRequirementsCardProps {
  docRequirements?: SiteRFPDetails['documentRequirements']
  customerRFPId?: number
  siteRFPId: number
  hasContractsType: boolean
  isFetching: boolean
}
